import React, { useState, useRef } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNodeTypeRegistry } from '../NodeTypeContext';
import { getNodeTypeInfo } from '../editorUtils';

// Simple JS syntax highlighter — returns HTML string with span-wrapped tokens
const highlightJS = (code) => {
  return code
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Comments
    .replace(/(\/\/.*)/g, '<span style="color:#6b7280">$1</span>')
    // Strings (single and double quoted, backticks)
    .replace(/('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"|`(?:[^`\\]|\\.)*`)/g, '<span style="color:#a5d6ff">$1</span>')
    // Keywords
    .replace(/\b(const|let|var|function|return|if|else|for|while|of|in|new|class|async|await|try|catch|throw|typeof|instanceof|switch|case|break|default|export|import|from)\b/g, '<span style="color:#ff7b72">$1</span>')
    // Built-in values
    .replace(/\b(true|false|null|undefined|this|NaN|Infinity)\b/g, '<span style="color:#79c0ff">$1</span>')
    // Numbers
    .replace(/\b(\d+\.?\d*)\b/g, '<span style="color:#d2a8ff">$1</span>')
    // Method calls
    .replace(/\.(\w+)\(/g, '.<span style="color:#d2a8ff">$1</span>(');
};

// Fullscreen code modal
const CodeModal = ({ value, onChange, language, onClose }) => {
  const highlighted = highlightJS(value || '');
  const [editing, setEditing] = useState(true);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-gray-900 rounded-xl shadow-2xl border border-gray-700 flex flex-col"
        style={{ width: '90vw', height: '85vh', maxWidth: '1200px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-800 border-b border-gray-700 rounded-t-xl">
          <span className="text-xs text-gray-400 font-mono">{language}</span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setEditing(!editing)}
              className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
            >
              {editing ? 'Preview' : 'Edit'}
            </button>
            <button
              onClick={onClose}
              className="text-xs text-gray-400 hover:text-white transition-colors"
            >
              Close
            </button>
          </div>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-hidden">
          {editing ? (
            <textarea
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full h-full px-5 py-4 bg-gray-900 text-gray-100 text-sm font-mono leading-relaxed resize-none focus:outline-none"
              spellCheck={false}
              autoFocus
              style={{ tabSize: 2 }}
            />
          ) : (
            <pre
              className="w-full h-full px-5 py-4 bg-gray-900 text-sm font-mono leading-relaxed whitespace-pre-wrap break-words overflow-y-auto"
              style={{ tabSize: 2, color: '#e6edf3' }}
              // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml -- highlightJS() HTML-escapes the input before wrapping in <span>s; no user HTML reaches innerHTML
              dangerouslySetInnerHTML={{ __html: highlighted || '\n' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Code editor with syntax highlighting
const CodeEditor = ({ value, onChange, language }) => {
  const preRef = useRef(null);
  const lineCount = (value || '').split('\n').length;
  const highlighted = highlightJS(value || '');
  const [editing, setEditing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <div className="rounded-lg overflow-hidden border border-gray-700">
        <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800 border-b border-gray-700">
          <span className="text-[10px] text-gray-400 font-mono">{language}</span>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500">{lineCount} lines</span>
            <button
              onClick={() => setEditing(!editing)}
              className="text-[10px] text-violet-400 hover:text-violet-300 transition-colors"
            >
              {editing ? 'Preview' : 'Edit'}
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="text-[10px] text-violet-400 hover:text-violet-300 transition-colors"
            >
              Expand
            </button>
          </div>
        </div>
        {editing ? (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-4 py-3 bg-gray-900 text-gray-100 text-xs font-mono leading-relaxed resize-y focus:outline-none"
            spellCheck={false}
            autoFocus
            style={{ tabSize: 2, minHeight: '300px' }}
          />
        ) : (
          <pre
            ref={preRef}
            onClick={() => setEditing(true)}
            className="px-4 py-3 bg-gray-900 text-xs font-mono leading-relaxed whitespace-pre-wrap break-words overflow-y-auto cursor-text"
            style={{ tabSize: 2, color: '#e6edf3', maxHeight: '500px' }}
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml -- highlightJS() HTML-escapes the input before wrapping in <span>s; no user HTML reaches innerHTML
            dangerouslySetInnerHTML={{ __html: highlighted || '\n' }}
          />
        )}
      </div>

      {modalOpen && (
        <CodeModal
          value={value}
          onChange={onChange}
          language={language}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

// Check if a property should be visible based on its displayOptions and current parameter values
const isPropertyVisible = (prop, parameters, nodeVersion) => {
  if (!prop.displayOptions) return true;

  // Always show if the parameter already has a value set
  if (parameters[prop.name] !== undefined && parameters[prop.name] !== '' && parameters[prop.name] !== null) {
    return true;
  }

  const { show, hide } = prop.displayOptions;

  if (show) {
    for (const [key, allowedValues] of Object.entries(show)) {
      // @version checks against the node type version, not parameters
      if (key === '@version') {
        const matches = allowedValues.some((v) => v === nodeVersion);
        if (!matches) return false;
        continue;
      }
      const currentValue = parameters[key];
      if (currentValue === undefined) return false;
      const matches = allowedValues.some((v) => {
        if (typeof v === 'boolean') return currentValue === v;
        return String(currentValue) === String(v);
      });
      if (!matches) return false;
    }
  }

  if (hide) {
    for (const [key, hiddenValues] of Object.entries(hide)) {
      if (key === '@version') {
        const matches = hiddenValues.some((v) => v === nodeVersion);
        if (matches) return false;
        continue;
      }
      const currentValue = parameters[key];
      if (currentValue === undefined) continue;
      const matches = hiddenValues.some((v) => {
        if (typeof v === 'boolean') return currentValue === v;
        return String(currentValue) === String(v);
      });
      if (matches) return false;
    }
  }

  return true;
};

// Strip HTML tags from descriptions
const stripHtml = (str) => {
  let result = str || '';
  let previous;
  do {
    previous = result;
    result = result.replace(/<[^>]*>/g, '');
  } while (result !== previous);
  return result;
};

// Detect if a string property is a code field
const isCodeField = (prop) => {
  const name = (prop.name || '').toLowerCase();
  return name.includes('code') || name.includes('jscode') || name.includes('pythoncode');
};

// Skip internal / non-user-facing property types
const SKIP_TYPES = new Set(['notice', 'curlImport', 'credentials', 'credentialsSelect', 'hidden', 'button']);

const formatLabel = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

// Custom dropdown for operator selects — avoids native <select> rendering issues
const OperatorSelect = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find(([v]) => v === value);
  const label = selected ? selected[1] : value;

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative min-w-0 flex-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white hover:border-gray-400 transition-colors truncate"
      >
        <span className="truncate">{label}</span>
        <ChevronDownIcon className="w-3 h-3 flex-shrink-0 text-gray-400 ml-1" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
          {options.map(([v, l]) => (
            <button
              key={v}
              type="button"
              onClick={() => { onChange(v); setOpen(false); }}
              className={`w-full text-left px-2 py-1.5 text-xs hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors ${
                v === value ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 font-medium' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SchemaField = ({ prop, value, onChange }) => {
  const [expanded, setExpanded] = useState(false);
  const label = prop.displayName || formatLabel(prop.name);

  switch (prop.type) {
    case 'options': {
      const options = (prop.options || []).map((o) =>
        typeof o === 'object' ? { value: o.value || o.name, label: o.name || o.value } : { value: o, label: String(o) }
      );
      return (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {label}
          </label>
          <select
            value={value ?? prop.default ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {prop.description && (
            <p className="mt-1 text-[10px] text-gray-400">{stripHtml(prop.description)}</p>
          )}
        </div>
      );
    }

    case 'boolean':
      return (
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {label}
            </label>
            <button
              onClick={() => onChange(!(value ?? prop.default ?? false))}
              className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                (value ?? prop.default ?? false) ? 'bg-violet-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${
                (value ?? prop.default ?? false) ? 'translate-x-4' : ''
              }`} />
            </button>
          </div>
          {prop.description && (
            <p className="mt-1 text-[10px] text-gray-400">{stripHtml(prop.description)}</p>
          )}
        </div>
      );

    case 'number':
      return (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {label}
          </label>
          <input
            type="number"
            value={value ?? prop.default ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder={prop.placeholder || ''}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs font-mono text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
          />
          {prop.description && (
            <p className="mt-1 text-[10px] text-gray-400">{stripHtml(prop.description)}</p>
          )}
        </div>
      );

    case 'json':
      return (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {label}
          </label>
          <textarea
            value={typeof value === 'string' ? value : JSON.stringify(value ?? prop.default ?? '', null, 2)}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs font-mono text-gray-900 dark:text-white resize-vertical focus:outline-none focus:ring-1 focus:ring-violet-500"
            spellCheck={false}
          />
          {prop.description && (
            <p className="mt-1 text-[10px] text-gray-400">{stripHtml(prop.description)}</p>
          )}
        </div>
      );

    case 'collection': {
      // Collection is a group of optional fields the user can toggle on
      const collectionOptions = prop.options || [];
      const currentObj = value && typeof value === 'object' ? value : {};
      return (
        <div className="mb-4">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 hover:text-violet-600 transition-colors"
          >
            {expanded ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
            {label}
            {Object.keys(currentObj).length > 0 && (
              <span className="font-normal text-gray-400 ml-1">({Object.keys(currentObj).length})</span>
            )}
          </button>
          {expanded && (
            <div className="ml-3 pl-3 border-l-2 border-gray-200 dark:border-gray-700 space-y-3">
              {collectionOptions.filter((o) => !SKIP_TYPES.has(o.type)).map((opt) => (
                <SchemaField
                  key={opt.name}
                  prop={opt}
                  value={currentObj[opt.name]}
                  onChange={(newVal) => {
                    const updated = { ...currentObj, [opt.name]: newVal };
                    onChange(updated);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    case 'fixedCollection': {
      const collectionOptions = prop.options || [];
      const currentObj = value && typeof value === 'object' ? value : {};
      return (
        <div className="mb-4">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 hover:text-violet-600 transition-colors"
          >
            {expanded ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
            {label}
          </button>
          {expanded && (
            <div className="ml-3 pl-3 border-l-2 border-gray-200 dark:border-gray-700 space-y-3">
              {collectionOptions.map((opt) => {
                const subValues = currentObj[opt.name] || [];
                return (
                  <div key={opt.name}>
                    <div className="text-[10px] font-medium text-gray-500 mb-1">{opt.displayName || formatLabel(opt.name)}</div>
                    {(opt.values || []).filter((v) => !SKIP_TYPES.has(v.type)).map((subProp) => (
                      <SchemaField
                        key={subProp.name}
                        prop={subProp}
                        value={Array.isArray(subValues) && subValues[0] ? subValues[0][subProp.name] : undefined}
                        onChange={(newVal) => {
                          const existing = Array.isArray(subValues) && subValues[0] ? { ...subValues[0] } : {};
                          existing[subProp.name] = newVal;
                          onChange({ ...currentObj, [opt.name]: [existing] });
                        }}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Filter type — used by IF node for conditions
    case 'filter': {
      const filterObj = value && typeof value === 'object' ? value : {};
      const conditions = filterObj.conditions || [];
      const combinator = filterObj.combinator || 'and';

      return (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
            {label}
          </label>

          {/* Combinator */}
          <div className="mb-3">
            <select
              value={combinator}
              onChange={(e) => onChange({ ...filterObj, combinator: e.target.value })}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
            >
              <option value="and">AND — all must match</option>
              <option value="or">OR — any must match</option>
            </select>
          </div>

          {/* Condition rows */}
          <div className="space-y-3">
            {conditions.map((cond, idx) => (
              <div key={cond.id || idx} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase">Condition {idx + 1}</span>
                  <button
                    onClick={() => {
                      const updated = { ...filterObj, conditions: conditions.filter((_, i) => i !== idx) };
                      onChange(updated);
                    }}
                    className="text-[10px] text-red-400 hover:text-red-600 transition-colors"
                  >
                    Remove
                  </button>
                </div>

                {/* Left value */}
                <div className="mb-2">
                  <label className="block text-[10px] text-gray-500 mb-1">Value</label>
                  <input
                    type="text"
                    value={cond.leftValue || ''}
                    onChange={(e) => {
                      const updated = [...conditions];
                      updated[idx] = { ...cond, leftValue: e.target.value };
                      onChange({ ...filterObj, conditions: updated });
                    }}
                    placeholder="e.g. {{ $json.field }}"
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs font-mono text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                    spellCheck={false}
                  />
                </div>

                {/* Operator — type */}
                <div className="mb-2">
                  <label className="block text-[10px] text-gray-500 mb-1">Type</label>
                  <OperatorSelect
                    value={cond.operator?.type || 'string'}
                    onChange={(val) => {
                      const upd = [...conditions];
                      upd[idx] = { ...cond, operator: { ...cond.operator, type: val } };
                      onChange({ ...filterObj, conditions: upd });
                    }}
                    options={[
                      ['string', 'String'], ['number', 'Number'], ['boolean', 'Boolean'],
                      ['array', 'Array'], ['object', 'Object'], ['dateTime', 'Date'],
                    ]}
                  />
                </div>

                {/* Operator — operation */}
                <div className="mb-2">
                  <label className="block text-[10px] text-gray-500 mb-1">Operation</label>
                  <OperatorSelect
                    value={cond.operator?.operation || 'equals'}
                    onChange={(val) => {
                      const upd = [...conditions];
                      upd[idx] = { ...cond, operator: { ...cond.operator, operation: val } };
                      onChange({ ...filterObj, conditions: upd });
                    }}
                    options={[
                      ['equals', 'equals'], ['notEquals', 'not equals'],
                      ['contains', 'contains'], ['notContains', 'not contains'],
                      ['startsWith', 'starts with'], ['endsWith', 'ends with'],
                      ['regex', 'regex'], ['exists', 'exists'], ['notExists', 'not exists'],
                      ['gt', '>'], ['gte', '>='], ['lt', '<'], ['lte', '<='],
                      ['empty', 'is empty'], ['notEmpty', 'is not empty'],
                    ]}
                  />
                </div>

                {/* Right value — hide for unary operators */}
                {!['exists', 'notExists', 'empty', 'notEmpty'].includes(cond.operator?.operation) && (
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Compare to</label>
                    <input
                      type="text"
                      value={cond.rightValue || ''}
                      onChange={(e) => {
                        const updated = [...conditions];
                        updated[idx] = { ...cond, rightValue: e.target.value };
                        onChange({ ...filterObj, conditions: updated });
                      }}
                      placeholder="Value to compare"
                      className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs font-mono text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                      spellCheck={false}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={() => {
              const newCondition = {
                id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
                leftValue: '',
                rightValue: '',
                operator: { type: 'string', operation: 'equals' },
              };
              onChange({ ...filterObj, conditions: [...conditions, newCondition] });
            }}
            className="mt-2 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-md transition-colors"
          >
            + Add condition
          </button>
        </div>
      );
    }

    // Multi-select options
    case 'multiOptions': {
      const options = (prop.options || []).map((o) =>
        typeof o === 'object' ? { value: o.value || o.name, label: o.name || o.value } : { value: o, label: String(o) }
      );
      const selected = Array.isArray(value) ? value : [];

      return (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {label}
          </label>
          <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800">
            {options.map((o) => (
              <label key={o.value} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  onChange={(e) => {
                    const updated = e.target.checked
                      ? [...selected, o.value]
                      : selected.filter((v) => v !== o.value);
                    onChange(updated);
                  }}
                  className="rounded border-gray-300 text-violet-500 focus:ring-violet-500"
                />
                {o.label}
              </label>
            ))}
          </div>
          {prop.description && (
            <p className="mt-1 text-[10px] text-gray-400">{stripHtml(prop.description)}</p>
          )}
        </div>
      );
    }

    // Resource locator — render as a simple string input
    case 'resourceLocator':
    case 'resourceMapper':
    case 'assignmentCollection':
    case 'workflowSelector': {
      const objValue = value && typeof value === 'object' ? value : {};
      return (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {label}
          </label>
          <textarea
            value={JSON.stringify(objValue, null, 2)}
            onChange={(e) => {
              try { onChange(JSON.parse(e.target.value)); } catch { /* ignore invalid JSON while typing */ }
            }}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs font-mono text-gray-900 dark:text-white resize-vertical focus:outline-none focus:ring-1 focus:ring-violet-500"
            spellCheck={false}
          />
          {prop.description && (
            <p className="mt-1 text-[10px] text-gray-400">{stripHtml(prop.description)}</p>
          )}
        </div>
      );
    }

    // String (default)
    default: {
      const rawValue = value ?? prop.default ?? '';
      // If the value is an object/array, render as JSON
      if (typeof rawValue === 'object' && rawValue !== null) {
        return (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {label}
            </label>
            <textarea
              value={JSON.stringify(rawValue, null, 2)}
              onChange={(e) => {
                try { onChange(JSON.parse(e.target.value)); } catch { /* ignore invalid JSON while typing */ }
              }}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-xs font-mono text-gray-900 dark:text-white resize-vertical focus:outline-none focus:ring-1 focus:ring-violet-500"
              spellCheck={false}
            />
            {prop.description && (
              <p className="mt-1 text-[10px] text-gray-400">{stripHtml(prop.description)}</p>
            )}
          </div>
        );
      }

      const strValue = String(rawValue);

      // Code editor style for code fields
      if (isCodeField(prop)) {
        return (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {label}
            </label>
            <CodeEditor
              value={strValue}
              onChange={onChange}
              language={prop.name.includes('python') ? 'Python' : 'JavaScript'}
            />
            {prop.description && (
              <p className="mt-1 text-[10px] text-gray-400">{stripHtml(prop.description)}</p>
            )}
          </div>
        );
      }

      const isMultiline = prop.typeOptions?.rows > 1 || strValue.length > 80 || strValue.includes('\n');
      const isExpression = strValue.startsWith('=') || strValue.includes('{{');

      return (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {label}
          </label>
          {isMultiline ? (
            <textarea
              value={strValue}
              onChange={(e) => onChange(e.target.value)}
              rows={prop.typeOptions?.rows || 3}
              placeholder={prop.placeholder || ''}
              className={`w-full px-3 py-2 border rounded-md text-xs font-mono resize-vertical focus:outline-none focus:ring-1 focus:ring-violet-500 ${
                isExpression
                  ? 'border-orange-300 dark:border-orange-600 bg-gray-900 text-orange-300'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
              }`}
              spellCheck={false}
            />
          ) : (
            <input
              type="text"
              value={String(strValue)}
              onChange={(e) => onChange(e.target.value)}
              placeholder={prop.placeholder || ''}
              className={`w-full px-3 py-2 border rounded-md text-xs font-mono focus:outline-none focus:ring-1 focus:ring-violet-500 ${
                isExpression
                  ? 'border-orange-300 dark:border-orange-600 bg-gray-900 text-orange-300'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
              }`}
              spellCheck={false}
            />
          )}
          {prop.description && (
            <p className="mt-1 text-[10px] text-gray-400">{stripHtml(prop.description)}</p>
          )}
        </div>
      );
    }
  }
};

const VisualParamsEditor = ({ parameters, onChange, nodeTypeKey }) => {
  const registry = useNodeTypeRegistry();
  const typeInfo = getNodeTypeInfo(nodeTypeKey, registry);
  const properties = typeInfo?.properties || [];
  const nodeVersion = typeInfo?.typeVersion || 1;
  // If typeVersion is an array (e.g. [1, 2]), use the latest
  const version = Array.isArray(nodeVersion) ? nodeVersion[nodeVersion.length - 1] : nodeVersion;

  // Deduplicate by name first (keep first entry per name), then filter
  const dedupedProps = [];
  const seenNames = new Set();
  for (const prop of properties) {
    if (seenNames.has(prop.name)) continue;
    seenNames.add(prop.name);
    dedupedProps.push(prop);
  }

  const visibleProps = dedupedProps.filter((prop) => {
    if (SKIP_TYPES.has(prop.type)) return false;
    if (!prop.displayName && !prop.name) return false;
    return isPropertyVisible(prop, parameters, version);
  });

  const handleChange = (paramName, newValue) => {
    onChange({ ...parameters, [paramName]: newValue });
  };

  if (visibleProps.length === 0) {
    return (
      <div className="text-center py-4 text-xs text-gray-400">
        No parameters available
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {visibleProps.map((prop) => (
        <SchemaField
          key={prop.name}
          prop={prop}
          value={parameters[prop.name]}
          onChange={(newVal) => handleChange(prop.name, newVal)}
        />
      ))}
    </div>
  );
};

export default VisualParamsEditor;
